import React from 'react';
import {
  ArrayResolution,
  ConditionType,
  defaultValue,
  Filter,
  filterOperatorOptions,
  getCondition,
  getFilterOptionsWithLegacy,
  getOperator,
  getRelationalExpressionProps,
  isNullOperator,
  operatorLabels,
  Select,
  selectorWidth,
  StringConditionType,
  supportParserOperatorOptions,
} from '@fabric-ui/components';
import { emptyObject } from '@fabric-ui/utils';
import { ANY_VALUE } from '../constants';
import { useExpressionBuilder } from '../ExpressionBuilderContext';
import { useFilterConfig, useFilterContext } from '../FilterContext';

interface OperatorSelectorSectionProps {
  actualFilterValue: Filter;
  enableFilter: boolean;
  fieldName?: string | null;
  filter: Filter;
  isOperatorLockedByToken: boolean;
  isRepeated: boolean;
  setCorrectFilter: (filter: Filter) => void;
  showListTab: boolean;
  typeCondition: ConditionType;
  typeName: string;
}

export const OperatorSelectorSection: React.FC<OperatorSelectorSectionProps> = React.memo(
  ({
    actualFilterValue,
    enableFilter,
    fieldName,
    filter,
    isOperatorLockedByToken,
    isRepeated,
    setCorrectFilter,
    showListTab,
    typeCondition,
    typeName,
  }) => {
    const {
      operatorOptions = filterOperatorOptions,
      relationsProps = emptyObject,
      supportParser = false,
      tokenSuggestions,
    } = useExpressionBuilder();
    const { disabled, isVertical, size, fullWidth } = useFilterConfig();
    const { accountEntities } = useFilterContext();
    const fieldMap = accountEntities?.fieldMap ?? {};
    const operator = getCondition(actualFilterValue, typeCondition, supportParser ? StringConditionType.EQUALS : undefined);
    const baseTypeOptions = operatorOptions[typeName];
    const tokenAwareOperatorOptions = baseTypeOptions
      ? getFilterOptionsWithLegacy(baseTypeOptions, operator).filter(option => {
          if (baseTypeOptions.some(base => base.value === option.value)) return true;
          return !tokenSuggestions?.some(token => token.operator === option.value);
        })
      : [];

    const setOperator = (compoundValue: string) => {
      const [value, resolution = ArrayResolution.ANY] = compoundValue.split('|') as [string, ArrayResolution | undefined];
      const [currentValue] = operator.split('|');
      const underlying = {
        fieldName,
        [typeCondition]: {
          [value]: isNullOperator(value)
            ? {}
            : isNullOperator(currentValue)
              ? fieldName
                ? defaultValue[typeCondition]?.(fieldMap[fieldName])
                : undefined
              : (filter.expression?.arrayCondition?.underlying[typeCondition]?.[currentValue] ??
                filter.expression?.[typeCondition]?.[currentValue]),
        },
        ...getRelationalExpressionProps({
          operator: value,
          typeCondition,
          enableCaseSensitive: !!relationsProps.enableCaseSensitive,
          enableSide: !!relationsProps.enableSide,
        }),
      };
      setCorrectFilter({
        expression: isRepeated && typeCondition !== ConditionType.field ? { arrayCondition: { underlying, resolution } } : underlying,
      });
    };

    const options = (() => {
      if (
        typeCondition === ConditionType.date &&
        actualFilterValue.expression?.dateCondition &&
        Object.keys(actualFilterValue.expression.dateCondition).length === 0 &&
        (actualFilterValue.expression?.stringCondition?.empty || actualFilterValue.expression?.stringCondition?.notEmpty)
      ) {
        return [];
      }
      return supportParser
        ? filter.expression?.fieldName === ANY_VALUE
          ? [
              { value: StringConditionType.CONTAINS, title: operatorLabels.contains },
              { value: StringConditionType.NOT_CONTAINS, title: operatorLabels.notContains },
            ]
          : supportParserOperatorOptions.string
        : tokenAwareOperatorOptions;
    })();

    if (showListTab) return null;

    const singleOperatorOption = baseTypeOptions?.length === 1 ? baseTypeOptions[0] : undefined;
    const width = isVertical ? selectorWidth : fullWidth ? '100%' : 150;
    const minWidth = fullWidth && !isVertical ? 120 : undefined;

    return isOperatorLockedByToken || singleOperatorOption ? (
      <Select
        size={size}
        style={{ width, minWidth, input: { fontWeight: 600 } }}
        muiProps={{ disablePortal: true }}
        value={singleOperatorOption?.value ?? operator}
        onChange={setOperator}
        disabled
        options={[singleOperatorOption ?? { value: operator, title: operatorLabels[operator] ?? operator }]}
      />
    ) : (
      <Select
        size={size}
        style={{ width, minWidth, input: { fontWeight: 600 } }}
        isRequired
        muiProps={{ disablePortal: true }}
        placeholder="Select Operator"
        value={getOperator(operator)}
        onChange={setOperator}
        disabled={!enableFilter || typeCondition === ConditionType.date || !fieldName || disabled}
        options={options}
      />
    );
  }
);
